import SEO, { breadcrumbSchema } from "../components/SEO";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import { SectionHead } from "../components/Cards";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import { SITE, img, IMAGES } from "../data/site";
import { ALL_IMAGES } from "../data/content";
import { Compass, Car, UserCheck, CalendarRange, MapPinned, Luggage } from "lucide-react";

const VALUES = [
  { icon: Compass, t: "Local Rajasthan Knowledge", d: "We are based in Jodhpur and drive these roads daily — the shortcuts, the clean dhabas, the right photo stops and the honest travel times." },
  { icon: Car, t: "Comfortable Vehicles", d: "Well-maintained, air-conditioned cars cleaned before every trip, from sedans for two to tempo travellers for the whole clan." },
  { icon: UserCheck, t: "Professional Drivers", d: "Courteous, experienced drivers who treat your family like their own and your schedule like their own." },
  { icon: CalendarRange, t: "Flexible Travel Options", d: "One-way drops, round trips, hourly cabs, multi-day tours — change of plans? We adjust." },
  { icon: MapPinned, t: "Local & Outstation", d: "From a 2 km market run in Jodhpur to a 12-day Rajasthan circuit, the same standard of service applies." },
  { icon: Luggage, t: "Tourist Transportation", d: "Airport and station pickups, hotel transfers, sightseeing days and desert-camp drops — the complete tourist journey covered." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Us — Blue City Taxi, Jodhpur"
        description="Blue City Taxi is a reliable taxi service based in Jodhpur, Rajasthan, serving travellers across the state with comfortable vehicles, professional drivers and flexible travel options."
        path="/about"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }])]}
      />
      <PageHero
        title="About Blue City Taxi"
        subtitle="A reliable taxi service based in Jodhpur, serving travellers across Rajasthan."
        image={IMAGES.mehrangarh}
        crumbs={[{ label: "Home", to: "/" }, { label: "About Us" }]}
      />
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold-dark">Our Story</p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold text-navy tracking-tight">
            From the Blue City, For All of Rajasthan
          </h2>
          <div className="mt-5 space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
            <p>
              Blue City Taxi began with a simple observation in Jodhpur: travellers landing in Rajasthan deserved
              a cab service that felt like a knowledgeable local friend — not a gamble. So we built one, from
              our home near Jhalamand, one honest trip at a time.
            </p>
            <p>
              Today we drive guests through every corner of the state — the blue lanes of our own city, the
              pink bazaars of Jaipur, Udaipur's lakesides, Jaisalmer's golden dunes and the quiet temple towns
              in between. What hasn't changed is how we work: clear quotes before you book, clean cars, and
              drivers who know the road and respect your time.
            </p>
            <p>
              Whether it's a 20-minute airport run or a two-week Rajasthan circuit, we plan it with the same
              care — because in our city, guests are <em>atithi</em>, and atithi devo bhava.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl border-2 border-gold/40 rotate-2" aria-hidden="true" />
            <img src={img(ALL_IMAGES.touristCar, 1000)} alt="Happy travellers with their Blue City Taxi cab on a Rajasthan road trip" loading="lazy" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[4/3]" />
          </div>
        </Reveal>
      </section>
      <Marquee />
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionHead eyebrow="What Drives Us" title="The Blue City Way of Travel" />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={(i % 3) * 0.08}>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 h-full hover:shadow-lg hover:border-royal/25 transition-all">
                <v.icon size={22} className="text-royal" />
                <h3 className="mt-3 font-heading font-bold text-navy">{v.t}</h3>
                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <CtaBand title="Come, travel Rajasthan with us." text={`Call ${SITE.phoneDisplay} or WhatsApp us — tell us where you want to go, and we'll take care of the road.`} />
    </>
  );
}
